import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { connect, useDispatch, useSelector } from "react-redux";
import {
  getSupportConversationDetails,
  sendSupportMessage,
  sendSupportImage,
  updateSupportStatus,
  updateSupportPriority,
  addInternalNote,
  deleteSupportMessage,
  clearSupportConversationDetails,
  markSupportMessagesAsRead,
} from "../../store/supportChat/supportChat.action";
import {
  RECEIVE_SUPPORT_MESSAGE_VIA_SOCKET,
  SET_USER_TYPING,
} from "../../store/supportChat/supportChat.type";
import {
  initSocket,
  getSocket,
  joinSupportChatRoom,
  leaveSupportChatRoom,
} from "../../../util/socket";
import dayjs from "dayjs";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendIcon from "@mui/icons-material/Send";
import DeleteIcon from "@mui/icons-material/Delete";
import NoteIcon from "@mui/icons-material/Note";
import PersonIcon from "@mui/icons-material/Person";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import formatImageUrl from "../../extra/functions";
import defaultImage from "../../../assets/images/default.jpg";

// Render http(s)/www URLs inside a message as clickable links (2026-08-26 —
// links in chats used to render as plain text). Opens in a new tab.
const LINK_RX = /((?:https?:\/\/|www\.)[^\s<>()]+[^\s<>().,;:!?'"\]])/gi;
function linkify(text) {
  if (!text || typeof text !== "string") return text;
  const parts = text.split(LINK_RX);
  return parts.map((part, i) => {
    if (i % 2 === 1) {
      const href = /^https?:\/\//i.test(part) ? part : "https://" + part;
      return (
        <a key={i} href={href} target="_blank" rel="noopener noreferrer" style={{ color: "inherit", textDecoration: "underline", wordBreak: "break-all" }}>
          {part}
        </a>
      );
    }
    return part;
  });
}


const SupportChatDetails = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const conversationId = location.state;
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const [message, setMessage] = useState("");
  const [internalNote, setInternalNote] = useState("");
  const [showNoteModal, setShowNoteModal] = useState(false);

  const { conversationDetails, messages, isUserTyping } = useSelector(
    (state) => state.supportChat
  );
  const { admin } = useSelector((state) => state.admin);

  // Initialize socket and set up listeners
  useEffect(() => {
    if (!admin?._id) return;

    // Initialize socket connection
    const socket = initSocket(admin._id);

    // Set up socket event listeners
    const handleNewMessage = (data) => {
      console.log(">>> Admin received newSupportMessage:", data);
      try {
        // Handle if data is wrapped in a message object
        let messageData;
        if (typeof data === "string") {
          messageData = JSON.parse(data);
        } else if (data && data.message) {
          messageData = data.message;
        } else {
          messageData = data;
        }

        dispatch({
          type: RECEIVE_SUPPORT_MESSAGE_VIA_SOCKET,
          payload: messageData,
        });
      } catch (error) {
        console.error("Error handling new support message:", error);
      }
    };

    const handleTyping = (data) => {
      console.log(">>> Admin received supportUserTyping:", data);
      try {
        const typingData = typeof data === "string" ? JSON.parse(data) : data;
        dispatch({
          type: SET_USER_TYPING,
          payload: typingData,
        });
      } catch (error) {
        console.error("Error handling typing indicator:", error);
      }
    };

    socket.on("newSupportMessage", handleNewMessage);
    socket.on("supportUserTyping", handleTyping);

    return () => {
      socket.off("newSupportMessage", handleNewMessage);
      socket.off("supportUserTyping", handleTyping);
    };
  }, [admin?._id, dispatch]);

  // Load conversation details and join room
  useEffect(() => {
    if (conversationId) {
      dispatch(getSupportConversationDetails(conversationId));
      // Mark messages as read when admin opens the conversation
      dispatch(markSupportMessagesAsRead(conversationId));
    }

    return () => {
      dispatch(clearSupportConversationDetails());
    };
  }, [dispatch, conversationId]);

  // Join/leave socket room when conversation changes
  useEffect(() => {
    if (conversationId && admin?._id) {
      joinSupportChatRoom(conversationId, admin._id);
    }

    return () => {
      if (conversationId && admin?._id) {
        leaveSupportChatRoom(conversationId, admin._id);
      }
    };
  }, [conversationId, admin?._id]);

  useEffect(() => {
    if (conversationDetails) {
      setInternalNote(conversationDetails.internalNotes || "");
    }
  }, [conversationDetails]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!message.trim()) return;

    dispatch(
      sendSupportMessage(conversationId, admin._id, "Admin", message.trim())
    );
    setMessage("");
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("Please select an image file");
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert("Image size must be less than 5MB");
      return;
    }

    dispatch(sendSupportImage(conversationId, admin._id, "Admin", file));

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleStatusChange = (status) => {
    dispatch(updateSupportStatus(conversationId, status, admin._id));
  };

  const handlePriorityChange = (priority) => {
    dispatch(updateSupportPriority(conversationId, priority));
  };

  const handleSaveNote = () => {
    dispatch(addInternalNote(conversationId, internalNote));
    setShowNoteModal(false);
  };

  const handleDeleteMessage = (messageId) => {
    if (window.confirm("Are you sure you want to delete this message?")) {
      dispatch(deleteSupportMessage(messageId));
    }
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      open: "bg-primary",
      pending: "bg-warning text-dark",
      resolved: "bg-success",
      closed: "bg-secondary",
    };
    return classes[status] || "bg-secondary";
  };

  const getPriorityBadgeClass = (priority) => {
    const classes = {
      low: "bg-info",
      medium: "bg-primary",
      high: "bg-warning text-dark",
      urgent: "bg-danger",
    };
    return classes[priority] || "bg-secondary";
  };

  if (!conversationDetails) {
    return (
      <div className="mainUserTable">
        <div className="userTable">
          <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mainUserTable">
      <div className="userTable">
        {/* Header */}
        <div className="col-12 headname d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <button
              className="btn btn-link text-white p-0 me-3"
              onClick={() => navigate("/admin/supportChat")}
            >
              <ArrowBackIcon />
            </button>
            <div>
              <span className="d-flex align-items-center">
                <SupportAgentIcon sx={{ mr: 1 }} /> Support Conversation
              </span>
              <small style={{ opacity: 0.7 }}>
                with {conversationDetails.user?.fullName || "User"}
              </small>
            </div>
          </div>
          <div className="d-flex gap-2 me-3" style={{ flexShrink: 0 }}>
            <span className={`badge ${getStatusBadgeClass(conversationDetails.status)}`}>
              {conversationDetails.status}
            </span>
            <span className={`badge ${getPriorityBadgeClass(conversationDetails.priority)}`}>
              {conversationDetails.priority}
            </span>
          </div>
        </div>

        <div className="userMain">
          <div className="row" style={{ minHeight: "calc(100vh - 250px)" }}>
            {/* Chat Area */}
            <div className="col-md-8 d-flex flex-column">
              {/* Messages */}
              <div className="card" style={{ backgroundColor: "#fff", border: "1px solid #e0e0e0", boxShadow: "0 2px 4px rgba(0,0,0,0.1)", flex: 1 }}>
                <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: "#F0C420", borderBottom: "1px solid #e0c41a" }}>
                  <h6 className="mb-0" style={{ color: "#333", fontWeight: "600" }}>Messages</h6>
                </div>
                <div
                  className="card-body"
                  style={{
                    overflowY: "auto",
                    backgroundColor: "#f8f9fa",
                    maxHeight: "450px",
                  }}
                >
                  {messages.length === 0 ? (
                    <div className="text-center py-5" style={{ color: "#666" }}>
                      No messages yet. Start the conversation!
                    </div>
                  ) : (
                    messages.map((msg, index) => {
                      const isAdmin = msg.senderType === "Admin";
                      const isSystem = msg.isSystemMessage;

                      if (isSystem) {
                        return (
                          <div key={msg._id} className="text-center my-2">
                            <small style={{ backgroundColor: "#e0e0e0", color: "#666", padding: "4px 12px", borderRadius: "12px" }}>
                              {msg.content}
                            </small>
                          </div>
                        );
                      }

                      return (
                        <div
                          key={msg._id}
                          className={`d-flex mb-3 ${isAdmin ? "justify-content-end" : "justify-content-start"}`}
                        >
                          {!isAdmin && (
                            <div
                              className="rounded-circle d-flex align-items-center justify-content-center me-2"
                              style={{ width: 35, height: 35, flexShrink: 0, backgroundColor: "#3498db" }}
                            >
                              <PersonIcon sx={{ fontSize: 20, color: "white" }} />
                            </div>
                          )}
                          <div
                            className="p-3 rounded-3"
                            style={{
                              maxWidth: "70%",
                              position: "relative",
                              backgroundColor: isAdmin ? "#F0C420" : "#3498db",
                              color: isAdmin ? "#333" : "#fff",
                              boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
                            }}
                          >
                            {msg.messageType === "image" ? (
                              <img
                                src={formatImageUrl(msg.content)}
                                alt="attachment"
                                style={{ maxWidth: "100%", borderRadius: 8, cursor: "pointer" }}
                                onError={(e) => { e.target.onerror = null; e.target.src = defaultImage; }}
                                onClick={() => window.open(formatImageUrl(msg.content), "_blank")}
                              />
                            ) : (
                              <div>{linkify(msg.content)}</div>
                            )}
                            <div className="d-flex align-items-center justify-content-between mt-1">
                              <small style={{ opacity: 0.7 }}>
                                {dayjs(msg.createdAt).format("HH:mm")}
                              </small>
                              {isAdmin && (
                                <DeleteIcon
                                  sx={{ fontSize: 16, cursor: "pointer", ml: 1, color: "rgba(0,0,0,0.5)" }}
                                  onClick={() => handleDeleteMessage(msg._id)}
                                />
                              )}
                            </div>
                          </div>
                          {isAdmin && (
                            <div
                              className="rounded-circle d-flex align-items-center justify-content-center ms-2"
                              style={{ width: 35, height: 35, flexShrink: 0, backgroundColor: "#F0C420" }}
                            >
                              <SupportAgentIcon sx={{ fontSize: 20, color: "#333" }} />
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </div>

              {/* Typing Indicator */}
              {isUserTyping && (
                <div className="d-flex align-items-center mt-2 ms-2">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center me-2"
                    style={{ width: 25, height: 25, backgroundColor: "#3498db" }}
                  >
                    <PersonIcon sx={{ fontSize: 14, color: "white" }} />
                  </div>
                  <small style={{ color: "#666", fontStyle: "italic" }}>
                    User is typing...
                  </small>
                </div>
              )}

              {/* Message Input */}
              <form onSubmit={handleSendMessage} className="mt-3">
                <div className="input-group">
                  {/* Hidden file input */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    accept="image/*"
                    style={{ display: "none" }}
                  />
                  {/* Image upload button */}
                  <button
                    type="button"
                    className="btn"
                    style={{ backgroundColor: "#f8f9fa", border: "1px solid #e0e0e0", color: "#666" }}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={conversationDetails.status === "closed"}
                    title="Send image"
                  >
                    <AttachFileIcon />
                  </button>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Type your response..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    disabled={conversationDetails.status === "closed"}
                    style={{ backgroundColor: "#fff", border: "1px solid #e0e0e0", color: "#333" }}
                  />
                  <button
                    type="submit"
                    className="btn"
                    style={{ backgroundColor: "#F0C420", color: "#333", border: "1px solid #e0c41a" }}
                    disabled={!message.trim() || conversationDetails.status === "closed"}
                  >
                    <SendIcon />
                  </button>
                </div>
                {conversationDetails.status === "closed" && (
                  <small className="text-danger">
                    This conversation is closed. Reopen it to send messages.
                  </small>
                )}
              </form>
            </div>

            {/* Sidebar */}
            <div className="col-md-4 ps-3" style={{ overflowY: "auto" }}>
              {/* User Info */}
              <div className="card mb-3" style={{ backgroundColor: "#fff", border: "1px solid #e0e0e0", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
                <div className="card-body">
                  <h6 style={{ color: "#F0C420", fontWeight: "600" }} className="mb-3">User Information</h6>
                  <div className="d-flex align-items-center mb-3">
                    <img
                      src={conversationDetails.user?.image || "/dummy.png"}
                      alt="user"
                      className="rounded-circle me-3"
                      style={{ width: 50, height: 50, objectFit: "cover", border: "2px solid #F0C420" }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/dummy.png";
                      }}
                    />
                    <div>
                      <div style={{ color: "#333", fontWeight: "500" }}>
                        {conversationDetails.user?.fullName || "N/A"}
                      </div>
                      <small style={{ color: "#666" }}>
                        {conversationDetails.user?.email}
                      </small>
                    </div>
                  </div>
                  {conversationDetails.user?.phone && (
                    <div style={{ color: "#666" }}>
                      Phone: {conversationDetails.user.phone}
                    </div>
                  )}
                </div>
              </div>

              {/* Ticket Info */}
              <div className="card mb-3" style={{ backgroundColor: "#fff", border: "1px solid #e0e0e0", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
                <div className="card-body">
                  <h6 style={{ color: "#F0C420", fontWeight: "600" }} className="mb-3">Ticket Information</h6>
                  <div className="mb-2">
                    <small style={{ color: "#666" }}>Subject</small>
                    <div style={{ color: "#333" }}>
                      {conversationDetails.subject || "General Support"}
                    </div>
                  </div>
                  <div className="mb-2">
                    <small style={{ color: "#666" }}>Created</small>
                    <div style={{ color: "#333" }}>
                      {dayjs(conversationDetails.createdAt).format("DD MMM YYYY HH:mm")}
                    </div>
                  </div>
                  {conversationDetails.resolvedAt && (
                    <div className="mb-2">
                      <small style={{ color: "#666" }}>Resolved</small>
                      <div style={{ color: "#333" }}>
                        {dayjs(conversationDetails.resolvedAt).format("DD MMM YYYY HH:mm")}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="card mb-3" style={{ backgroundColor: "#fff", border: "1px solid #e0e0e0", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
                <div className="card-body">
                  <h6 style={{ color: "#F0C420", fontWeight: "600" }} className="mb-3">Actions</h6>

                  {/* Status */}
                  <div className="mb-3">
                    <label className="form-label" style={{ color: "#666" }}>Status</label>
                    <select
                      className="form-select form-select-sm"
                      value={conversationDetails.status}
                      onChange={(e) => handleStatusChange(e.target.value)}
                      style={{ backgroundColor: "#fff", border: "1px solid #e0e0e0", color: "#333" }}
                    >
                      <option value="open">Open</option>
                      <option value="pending">Pending</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>

                  {/* Priority */}
                  <div className="mb-3">
                    <label className="form-label" style={{ color: "#666" }}>Priority</label>
                    <select
                      className="form-select form-select-sm"
                      value={conversationDetails.priority}
                      onChange={(e) => handlePriorityChange(e.target.value)}
                      style={{ backgroundColor: "#fff", border: "1px solid #e0e0e0", color: "#333" }}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>

                  {/* Internal Note */}
                  <div>
                    <button
                      className="btn btn-sm w-100"
                      style={{ backgroundColor: "#f8f9fa", border: "1px solid #e0e0e0", color: "#333" }}
                      onClick={() => setShowNoteModal(true)}
                    >
                      <NoteIcon sx={{ mr: 1, fontSize: 18 }} />
                      Internal Note
                    </button>
                  </div>
                </div>
              </div>

              {/* Tags */}
              {conversationDetails.tags && conversationDetails.tags.length > 0 && (
                <div className="card mb-3" style={{ backgroundColor: "#fff", border: "1px solid #e0e0e0", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
                  <div className="card-body">
                    <h6 style={{ color: "#F0C420", fontWeight: "600" }} className="mb-3">Tags</h6>
                    <div className="d-flex flex-wrap gap-1">
                      {conversationDetails.tags.map((tag, index) => (
                        <span key={index} className="badge" style={{ backgroundColor: "#F0C420", color: "#333" }}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Internal Note Modal */}
      {showNoteModal && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ backgroundColor: "#fff" }}>
              <div className="modal-header" style={{ borderBottom: "1px solid #e0e0e0" }}>
                <h5 className="modal-title" style={{ color: "#333" }}>Internal Note</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowNoteModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <textarea
                  className="form-control"
                  rows={5}
                  placeholder="Add internal notes (only visible to support team)"
                  value={internalNote}
                  onChange={(e) => setInternalNote(e.target.value)}
                  style={{ backgroundColor: "#fff", border: "1px solid #e0e0e0", color: "#333" }}
                ></textarea>
              </div>
              <div className="modal-footer" style={{ borderTop: "1px solid #e0e0e0" }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowNoteModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn"
                  style={{ backgroundColor: "#F0C420", color: "#333" }}
                  onClick={handleSaveNote}
                >
                  Save Note
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default connect(null, {
  getSupportConversationDetails,
  sendSupportMessage,
  sendSupportImage,
  updateSupportStatus,
  updateSupportPriority,
  addInternalNote,
  deleteSupportMessage,
  clearSupportConversationDetails,
})(SupportChatDetails);
